/**
 * Obsidian syntax themes for Shiki (rehype-pretty-code).
 *
 * Single-identity pair anchored to the Obsidian black cut, the identity
 * this site stamps (layout.tsx). Both schemes draw the same restrained
 * four-hue mapping as the `--syntax-*` tokens in globals.css: the status
 * set carries the hue (secure blue for keywords and types, success green
 * for strings and numbers, warning ochre for callables and attributes,
 * destructive for tags), comments take the muted foreground, and plain
 * identifiers stay at the base foreground so prose-heavy code reads calm.
 * Hue marks structure only; the black cut's own ramp is silver, so the
 * accent ramp is not drawn here at all.
 *
 * Shiki cannot read CSS variables, so the values come from
 * src/lib/obsidian-palette.ts, which tests/lib/obsidian-palette.test.ts
 * holds to the vendored package. Every token clears WCAG AA (4.5:1) on the
 * scheme's code ground, `--card`, which is the visible surface because
 * keepBackground is off in mdx.tsx; the backgrounds here are informational.
 */

import { OBSIDIAN_DARK, OBSIDIAN_DERIVED, OBSIDIAN_LIGHT } from '@/lib/obsidian-palette';

interface ShikiThemeObject {
  name: string;
  type: 'light' | 'dark';
  colors: Record<string, string>;
  /** vscode-textmate raw-theme settings (Shiki's ThemeRegistrationRaw). */
  settings: Array<{
    scope?: string | string[];
    settings: { foreground?: string; background?: string; fontStyle?: string };
  }>;
}

export interface ShikiPalette {
  base: string;
  surface: string;
  comment: string;
  string: string;
  number: string;
  keyword: string;
  type: string;
  fn: string;
  attribute: string;
  tag: string;
  operator: string;
  punctuation: string;
}

const scopes = (palette: ShikiPalette): ShikiThemeObject['settings'] => [
  // Scope-less base entry: unscoped tokens take the identity foreground.
  { settings: { foreground: palette.base, background: palette.surface } },
  {
    scope: ['comment', 'punctuation.definition.comment'],
    settings: { foreground: palette.comment },
  },
  {
    scope: ['string', 'punctuation.definition.string', 'constant.other.symbol'],
    settings: { foreground: palette.string },
  },
  {
    scope: ['constant.numeric', 'constant.character'],
    settings: { foreground: palette.number },
  },
  {
    scope: [
      'keyword',
      'keyword.control',
      'storage.type',
      'storage.modifier',
      'constant.language',
      'variable.language',
    ],
    settings: { foreground: palette.keyword },
  },
  {
    scope: [
      'entity.name.type',
      'entity.name.class',
      'entity.name.namespace',
      'support.type',
      'support.class',
    ],
    settings: { foreground: palette.type },
  },
  {
    scope: ['entity.name.function', 'support.function', 'meta.function-call entity.name'],
    settings: { foreground: palette.fn },
  },
  {
    scope: ['entity.other.attribute-name'],
    settings: { foreground: palette.attribute },
  },
  {
    scope: ['entity.name.tag', 'punctuation.definition.tag'],
    settings: { foreground: palette.tag },
  },
  {
    scope: ['keyword.operator'],
    settings: { foreground: palette.operator },
  },
  {
    scope: ['punctuation', 'meta.brace'],
    settings: { foreground: palette.punctuation },
  },
  // Markdown fences
  {
    scope: ['markup.heading', 'punctuation.definition.heading'],
    settings: { foreground: palette.keyword, fontStyle: 'bold' },
  },
  { scope: ['markup.bold'], settings: { fontStyle: 'bold' } },
  { scope: ['markup.italic'], settings: { fontStyle: 'italic' } },
  { scope: ['markup.inline.raw', 'markup.fenced_code'], settings: { foreground: palette.string } },
];

/** Token anchors, light scheme: the cloth's card as ground. */
export const obsidianShikiLightPalette: ShikiPalette = {
  base: OBSIDIAN_LIGHT.foreground,
  surface: OBSIDIAN_LIGHT.card,
  comment: OBSIDIAN_LIGHT.mutedForeground,
  string: OBSIDIAN_LIGHT.success,
  number: OBSIDIAN_LIGHT.success,
  keyword: OBSIDIAN_LIGHT.secure,
  type: OBSIDIAN_LIGHT.secure,
  fn: OBSIDIAN_LIGHT.warning,
  attribute: OBSIDIAN_LIGHT.warning,
  tag: OBSIDIAN_LIGHT.destructive,
  operator: OBSIDIAN_DERIVED.syntaxOperatorLight,
  punctuation: OBSIDIAN_DERIVED.syntaxOperatorLight,
};

/** Token anchors, dark scheme: the glass's card as ground. */
export const obsidianShikiDarkPalette: ShikiPalette = {
  base: OBSIDIAN_DARK.foreground,
  surface: OBSIDIAN_DARK.card,
  comment: OBSIDIAN_DARK.mutedForeground,
  string: OBSIDIAN_DARK.success,
  number: OBSIDIAN_DARK.success,
  keyword: OBSIDIAN_DARK.secure,
  type: OBSIDIAN_DARK.secure,
  fn: OBSIDIAN_DARK.warning,
  attribute: OBSIDIAN_DARK.warning,
  tag: OBSIDIAN_DARK.destructive,
  operator: OBSIDIAN_DERIVED.syntaxOperatorDark,
  punctuation: OBSIDIAN_DERIVED.syntaxOperatorDark,
};

export const obsidianShikiLight: ShikiThemeObject = {
  name: 'obsidian-light',
  type: 'light',
  colors: {
    'editor.background': obsidianShikiLightPalette.surface,
    'editor.foreground': obsidianShikiLightPalette.base,
  },
  settings: scopes(obsidianShikiLightPalette),
};

export const obsidianShikiDark: ShikiThemeObject = {
  name: 'obsidian-dark',
  type: 'dark',
  colors: {
    'editor.background': obsidianShikiDarkPalette.surface,
    'editor.foreground': obsidianShikiDarkPalette.base,
  },
  settings: scopes(obsidianShikiDarkPalette),
};
